"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { useAuth } from "@/providers/auth-provider";
import { useMatureContentConfirmation } from "@/hooks/use-mature-content-confirmation";
import Link from "next/link";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
type GateProps = {
  storyId: string;
  storyTitle: string;
  isMature: boolean;
  children: ReactNode;
};
function CheckingConfirmation() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <main className="flex min-h-screen items-center justify-center p-6 text-muted-foreground"><p role="status">Checking content confirmation…</p></main>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** A content advisory, not identity or age verification. No reader mounts before consent. */
export function MatureContentGate(props) {
  const $ = _c(5);
  const {
    user,
    loading
  } = useAuth();
  if (!props.isMature) {
    return props.children;
  }
  if (loading) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <CheckingConfirmation />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  const t0 = `${user?.id ?? "guest"}:${props.storyId}`;
  const t1 = user?.id ?? null;
  let t2;
  if ($[1] !== props || $[2] !== t0 || $[3] !== t1) {
    t2 = <ConfirmationGate key={t0} {...props} userId={t1} />;
    $[1] = props;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function ConfirmationGate(t0) {
  const $ = _c(21);
  const {
    storyId,
    storyTitle,
    userId,
    children
  } = t0;
  const {
    accepted,
    checking,
    saving,
    error,
    confirm
  } = useMatureContentConfirmation(storyId, userId);
  if (checking) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CheckingConfirmation />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (accepted) {
    return children;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <main className="flex min-h-screen items-center justify-center p-6 text-muted-foreground"><p>This story is intended for readers aged 18 and older.</p></main>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AlertDialogTitle>Mature content · 18+</AlertDialogTitle>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== storyTitle) {
    t3 = <AlertDialogHeader>{t2}<AlertDialogDescription>The author has marked “{storyTitle}” as intended for adults. It may contain explicit sexual content, graphic violence, or other mature themes. Please confirm you are 18 or older before reading.</AlertDialogDescription></AlertDialogHeader>;
    $[3] = storyTitle;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== userId) {
    t4 = userId && <p className="text-xs text-muted-foreground">Your confirmation for this story will be saved to your account across devices.</p>;
    $[5] = userId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== error) {
    t5 = error && <p role="alert" className="text-sm text-destructive">{error}</p>;
    $[7] = error;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AlertDialogCancel asChild={true}><Link href="/">Leave story</Link></AlertDialogCancel>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== confirm) {
    t7 = () => void confirm();
    $[10] = confirm;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = saving ? "Saving\u2026" : "I\u2019m 18 or older \u2014 continue";
  let t9;
  if ($[12] !== saving || $[13] !== t7 || $[14] !== t8) {
    t9 = <AlertDialogFooter>{t6}<AlertDialogAction disabled={saving} onClick={t7}>{t8}</AlertDialogAction></AlertDialogFooter>;
    $[12] = saving;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t9) {
    t10 = <>{t1}<AlertDialog open={true}><AlertDialogContent onEscapeKeyDown={_temp}>{t3}{t4}{t5}{t9}</AlertDialogContent></AlertDialog></>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
function _temp(event) {
  return event.preventDefault();
}